import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, AtSign, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const RESERVED = /markersync/i;

function validate(value) {
  if (!value) return null;
  if (value.length < 3) return 'At least 3 characters';
  if (value.length > 20) return 'Max 20 characters';
  if (!/^[a-z0-9_]+$/.test(value)) return 'Only lowercase letters, numbers, and underscores';
  if (RESERVED.test(value)) return 'That username is not available';
  return null;
}

export default function UsernameModal({ isOpen, onClose }) {
  const { user, setUsername } = useAuth();
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [isCooldown, setIsCooldown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setValue(user?.username || '');
      setError('');
      setIsCooldown(false);
      setSaved(false);
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      return () => { document.documentElement.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const raw = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setValue(raw);
    setError('');
    setIsCooldown(false);
  };

  const handleSubmit = async () => {
    const validationError = validate(value);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');
    const result = await setUsername(value);
    setSaving(false);

    if (result.success) {
      setSaved(true);
      setTimeout(() => onClose(), 1500);
    } else {
      setError(result.error);
      setIsCooldown(!!result.cooldown);
    }
  };

  const formatHint = validate(value);
  const isUnchanged = value === (user?.username || '');

  return createPortal(
    <div
      className="fixed inset-0 z-[70] backdrop-blur-sm bg-black/30 flex items-center justify-center p-4 animate-fadeOnly"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-100 overflow-hidden animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <AtSign size={20} className="text-purple-500" />
              <h3 className="text-lg font-display font-semibold text-slate-800">
                {user?.username ? 'Change Username' : 'Set Username'}
              </h3>
            </div>
            <div className="w-16 h-1 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mt-2" />
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-6">
          {saved ? (
            <div className="py-8 text-center">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Check size={28} className="text-emerald-600" />
              </div>
              <p className="text-base font-semibold text-slate-800 mb-1">Username saved!</p>
              <p className="text-sm text-slate-500">@{value}</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-5 leading-relaxed">
                Pick a handle that represents you on MarkerSync.
              </p>

              <div className="mb-5">
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Username</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">@</span>
                  <input
                    type="text"
                    value={value}
                    onChange={handleChange}
                    placeholder="your_username"
                    maxLength={20}
                    autoFocus
                    className="w-full pl-7 pr-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent text-sm transition-all"
                    disabled={saving}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !saving) handleSubmit(); }}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  3–20 characters. Lowercase letters, numbers, underscores only.
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium transition-all"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving || !value || !!formatHint || isUnchanged}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-sm font-medium shadow-sm transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>

              {isCooldown && (
                <p className="text-center text-xs text-slate-400 mt-4">
                  Need help?{' '}
                  <a href="mailto:markersync@gmail.com" className="underline hover:text-slate-600 transition-colors">
                    Contact us
                  </a>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
